import * as React from 'react';
import { AlertTriangle, RefreshCw, Trash2, ShieldAlert, Zap, LogOut } from 'lucide-react';

interface Props {
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleResetStateAndReload = () => {
    try {
      localStorage.removeItem('playground_current_user');
      localStorage.removeItem('playground_is_logged_in');
      sessionStorage.clear();
    } catch (e) {
      console.error('Error clearing localStorage', e);
    }
    window.location.href = '/';
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-indigo-950 text-white flex items-center justify-center p-4 font-sans antialiased">
          <div className="max-w-md w-full bg-indigo-900/90 border border-white/10 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl space-y-6 text-center animate-fadeIn">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-500/20 text-rose-400 border border-rose-500/40 flex items-center justify-center shadow-inner">
              <AlertTriangle className="w-8 h-8 stroke-[2.5]" />
            </div>

            <div className="space-y-2">
              <div className="inline-block px-3 py-1 bg-lime-400/20 border border-lime-400/30 rounded-full text-[10px] font-black italic uppercase text-lime-300">
                Playground League System Recovery
              </div>
              <h2 className="text-2xl font-black italic uppercase text-white tracking-tight">
                Application Interrupted
              </h2>
              <p className="text-xs text-indigo-200/80 font-medium leading-relaxed">
                A temporary rendering issue occurred. Click below to reload the app or reset session state.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-indigo-950/80 p-3 rounded-xl border border-white/10 text-left overflow-x-auto text-[11px] font-mono text-rose-300 max-h-28 overflow-y-auto">
                {this.state.error.toString()}
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reload Playground League</span>
              </button>

              <button
                type="button"
                onClick={this.handleResetStateAndReload}
                className="w-full py-3 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold italic uppercase text-xs rounded-2xl border border-white/10 flex items-center justify-center space-x-2 transition"
              >
                <Trash2 className="w-4 h-4 text-rose-400" />
                <span>Reset Local Session & Restore Default</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface AuthErrorBoundaryProps {
  children?: React.ReactNode;
  onResetAuth?: () => void;
}

interface AuthErrorBoundaryState {
  hasAuthError: boolean;
  authError: Error | null;
}

export class AuthErrorBoundary extends React.Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = {
      hasAuthError: false,
      authError: null,
    };
  }

  public static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasAuthError: true, authError: error };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Authentication Error caught by AuthErrorBoundary:', error, errorInfo);
  }

  private handleRetryAuth = () => {
    this.setState({ hasAuthError: false, authError: null });
    if (this.props.onResetAuth) {
      this.props.onResetAuth();
    }
  };

  private handleClearSessionAndRetry = () => {
    try {
      localStorage.removeItem('playground_current_user');
      localStorage.removeItem('playground_is_logged_in');
      sessionStorage.clear();
    } catch (e) {
      console.error('Error clearing auth session storage', e);
    }
    this.setState({ hasAuthError: false, authError: null });
    if (this.props.onResetAuth) {
      this.props.onResetAuth();
    } else {
      window.location.reload();
    }
  };

  public override render() {
    if (this.state.hasAuthError) {
      return (
        <div className="min-h-screen bg-indigo-950 text-white flex items-center justify-center p-4 font-sans antialiased">
          <div className="max-w-md w-full bg-indigo-900/95 border border-rose-500/40 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl space-y-6 text-center animate-fadeIn">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-rose-500/20 text-rose-400 border border-rose-500/40 flex items-center justify-center shadow-inner">
              <ShieldAlert className="w-8 h-8 stroke-[2.5]" />
            </div>

            <div className="space-y-2">
              <div className="inline-block px-3 py-1 bg-rose-500/20 border border-rose-500/30 rounded-full text-[10px] font-black italic uppercase text-rose-300">
                Authentication System Error
              </div>
              <h2 className="text-xl font-black italic uppercase text-white tracking-tight">
                Authentication State Interrupted
              </h2>
              <p className="text-xs text-indigo-200/80 font-medium leading-relaxed">
                An issue occurred while initializing user authentication or loading profile state.
              </p>
            </div>

            {this.state.authError && (
              <div className="bg-indigo-950/80 p-3 rounded-xl border border-white/10 text-left overflow-x-auto text-[11px] font-mono text-rose-300 max-h-24 overflow-y-auto">
                {this.state.authError.toString()}
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={this.handleRetryAuth}
                className="w-full py-3.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition hover:scale-[1.02]"
              >
                <Zap className="w-4 h-4 fill-black" />
                <span>Retry Authentication & Re-sync</span>
              </button>

              <button
                type="button"
                onClick={this.handleClearSessionAndRetry}
                className="w-full py-3 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 font-bold italic uppercase text-xs rounded-2xl border border-white/10 flex items-center justify-center space-x-2 transition"
              >
                <LogOut className="w-4 h-4 text-rose-400" />
                <span>Reset Local Auth & Continue as Guest</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

